import axios from "axios";
import React from "react";
import { useEffect } from "react";
import { useState } from "react";
import { Button, Card, Col, Modal, ModalBody, Spinner } from "reactstrap";
import {
  Block,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  PaginationComponent,
} from "../../../components/Component";
import { MyCustomersTable, MyReferralsTable } from "../../../components/table/SpecialTable";
import { authHeader } from "../../../header";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import CopyToClipboard from "react-copy-to-clipboard";
import { message, Tabs } from "antd";
import { useDebounce } from "../../../hooks/useDebounceSearch";

export const ManageMyCustomers = () => {
  const [activeTab, setActiveTab] = useState("customer");

  const [isLoading, setIsLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customersPage, setCustomersPage] = useState(1);
  const [customersTotal, setCustomersTotal] = useState(0);

  const [isReferralsLoading, setIsReferralsLoading] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [referralsPage, setReferralsPage] = useState(1);
  const [referralsTotal, setReferralsTotal] = useState(0);

  const [referralLink, setReferralLink] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [modal, setModal] = useState({ referral: false, view: false });
  const [itemPerPage] = useState(7);
  const [copyLink, setCopyLink] = useState(false);
  const [copyCode, setCopyCode] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [search, setSearch] = useState("");
  const [getCurrentUser, setGetCurrentUser] = useState(null);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const debouncedSearchTerm = useDebounce(search, 500);

  // Re-run the search against whichever tab is currently active.
  useEffect(
    () => {
      if (activeTab === "customer") {
        setCustomersPage(1);
        getAllMyCustomers(1);
      } else {
        setReferralsPage(1);
        getAllMyReferrals(1);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearchTerm]
  );

  const getAllMyCustomers = async (page = customersPage) => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/admin/users/get-all-users-under-admin?search=${search}&pageNo=${page}&pageSize=${itemPerPage}`,
        {
          headers: authHeader(),
        }
      );
      const { data } = response;
      if (data) {
        const items = data.content ?? data.entities ?? (Array.isArray(data) ? data : []);
        setCustomers(items);
        setCustomersTotal(data.totalElements ?? items.length);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  const getAllMyReferrals = async (page = referralsPage) => {
    try {
      setIsReferralsLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/v3/referral-histories/admin?email=${getCurrentUser?.email}&pageNo=${page}&pageSize=${itemPerPage}`,
        {
          headers: authHeader(),
        }
      );
      const { data } = response;
      if (data) {
        setReferrals(data.content ?? []);
        setReferralsTotal(data.totalElements ?? 0);
        setIsReferralsLoading(false);
      }
    } catch (error) {
      setIsReferralsLoading(false);
    }
  };

  const buildReferralLink = (referralCode, referrerId) => {
    if (!referralCode) return "N/A";
    let link = `https://rosabon.onelink.me/pIvo?pid=af_app_invites&c=customer_referral&af_channel=referral&af_siteid=com.optisoft.rosabon`;
    if (referrerId != null) {
      link += `&af_referrer_uid=${encodeURIComponent(referrerId)}`;
    }
    link += `&deep_link_value=${encodeURIComponent(referralCode)}`;
    return link;
  };

  const getAdminReferralLink = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/auth/users`, {
        headers: authHeader(),
      });
      const { data } = response;
      if (data) {
        setGetCurrentUser(data);
        setReferralCode(data?.myReferralCode ?? "N/A");
        setReferralLink(buildReferralLink(data?.myReferralCode, data?.id));
      }
    } catch (error) {
      // Referral link/code stay at their initial empty state.
    }
  };

  const onViewClick = (data) => {
    setSelectedCustomer(data);
    setModal({ view: true });
  };

  const onFormCancel = () => {
    setModal({ edit: false, add: false });
    // resetForm();
  };

  const handleCopy = (type) => {
    if (type === "link") {
      setCopyLink(true);
    } else {
      setCopyCode(true);
    }
    message.success({
      content: `Successfully Copied Referral ${type}.`,
    });
    if (type === "link") {
      setTimeout(() => setCopyLink(false), 2000);
    } else {
      setTimeout(() => setCopyCode(false), 2000);
    }
  };

  useEffect(() => {
    getAdminReferralLink();
    getAllMyCustomers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (getCurrentUser) {
      getAllMyReferrals(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCurrentUser]);

  const paginateCustomers = (pageNumber) => {
    setCustomersPage(pageNumber);
    getAllMyCustomers(pageNumber);
  };

  const paginateReferrals = (pageNumber) => {
    setReferralsPage(pageNumber);
    getAllMyReferrals(pageNumber);
  };

  return (
    <React.Fragment>
      <Head title="My Customers"></Head>
      <Content>
        <Block size="lg">
          <BlockHead>
            <BlockHeadContent>
              <div className="d-flex justify-content-between align-items-center">
                <BlockTitle tag="h4">My Customers</BlockTitle>
                <div className="d-flex align-items-center justify-center">
                  <div className="mr-2">
                    <input
                      className="form-control"
                      value={search}
                      onChange={handleSearchChange}
                      placeholder={activeTab === "customer" ? "Search Customer..." : "Search Referral..."}
                    />
                  </div>
                  <Button color="primary" onClick={() => setModal({ referral: true })}>
                    <Icon name="eye"></Icon>
                    <span>View My Referral Link</span>
                  </Button>
                </div>
              </div>
            </BlockHeadContent>
          </BlockHead>

          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <Tabs.TabPane tab="My Customer" key="customer">
              <Card className="card-bordered card-preview" style={{ width: "100%", overflowX: "auto" }}>
                {isLoading ? (
                  <Spinner
                    color="primary"
                    style={{
                      height: "3rem",
                      width: "3rem",
                      margin: "0 auto",
                    }}
                  />
                ) : (
                  <MyCustomersTable onViewClick={onViewClick} data={customers} />
                )}
              </Card>
              <div className="card-inner">
                {customers && customers.length > 0 ? (
                  <PaginationComponent
                    itemPerPage={itemPerPage}
                    totalItems={customersTotal}
                    paginate={paginateCustomers}
                    currentPage={customersPage}
                  />
                ) : (
                  <div className="text-center">
                    <span className="text-silent">No Customer found</span>
                  </div>
                )}
              </div>
            </Tabs.TabPane>

            <Tabs.TabPane tab="My Referral" key="referral">
              <Card className="card-bordered card-preview" style={{ width: "100%", overflowX: "auto" }}>
                {isReferralsLoading ? (
                  <Spinner
                    color="primary"
                    style={{
                      height: "3rem",
                      width: "3rem",
                      margin: "0 auto",
                    }}
                  />
                ) : (
                  <MyReferralsTable data={referrals} />
                )}
              </Card>
              <div className="card-inner">
                {referrals && referrals.length > 0 ? (
                  <PaginationComponent
                    itemPerPage={itemPerPage}
                    totalItems={referralsTotal}
                    paginate={paginateReferrals}
                    currentPage={referralsPage}
                  />
                ) : (
                  <div className="text-center">
                    <span className="text-silent">No Referral found</span>
                  </div>
                )}
              </div>
            </Tabs.TabPane>
          </Tabs>
        </Block>
        <Modal isOpen={modal.referral} toggle={() => setModal({ referral: false })}>
          <ModalBody>
            <a
              href="#cancel"
              onClick={(ev) => {
                ev.preventDefault();
                onFormCancel();
              }}
              className="close"
            >
              <Icon name="cross-sm" />
            </a>
            <div className="p-2">
              <h5 className="title">My Referral Link</h5>
              <div className="mt-4">
                <div className="d-flex align-items-center justify-content-between">
                  <Col
                    md="8"
                    className="p-2 bg-gray text-white rounded"
                    style={{ maxWidth: "100%", minWidth: 0, overflow: "hidden" }}
                  >
                    <p style={{ wordBreak: "break-all", overflowWrap: "break-word", margin: 0 }}>{referralLink}</p>
                  </Col>
                  <Col md="2" className="mr-5">
                    <CopyToClipboard text={referralLink}>
                      <Button onClick={() => handleCopy("link")}>
                        <Icon name="copy" className="mr-2" />
                        <p>{copyLink ? "COPIED" : "COPY"}</p>
                      </Button>
                    </CopyToClipboard>
                  </Col>
                </div>
                <div className="d-flex align-items-center justify-content-between mt-4">
                  <Col
                    md="8"
                    className="p-2 bg-gray text-white rounded"
                    style={{ maxWidth: "100%", minWidth: 0, overflow: "hidden" }}
                  >
                    <p style={{ wordBreak: "break-all", overflowWrap: "break-word", margin: 0 }}>{referralCode}</p>
                  </Col>
                  <Col md="2" className="mr-5">
                    <CopyToClipboard text={referralCode}>
                      <Button onClick={() => handleCopy("code")}>
                        <Icon name="copy" className="mr-2" />
                        <p>{copyCode ? "COPIED" : "COPY"}</p>
                      </Button>
                    </CopyToClipboard>
                  </Col>
                </div>
              </div>
            </div>
          </ModalBody>
        </Modal>
        <Modal size="lg" isOpen={modal.view} toggle={() => setModal({ view: false })}>
          <ModalBody>
            <a
              href="#cancel"
              onClick={(ev) => {
                ev.preventDefault();
                onFormCancel();
              }}
              className="close"
            >
              <Icon name="cross-sm" />
            </a>
            <div className="p-2">
              <h5 className="title">Customer Details</h5>
              <Col md="12" className="w-100 d-flex justify-content-between p-0">
                <Col md="6" className="p-0">
                  <span style={{ fontSize: "1rem" }} className="font-weight-bold mr-1">
                    First Name:
                  </span>
                  <span style={{ fontSize: "0.9rem" }}>{selectedCustomer?.individualUser?.firstName ?? "N/A"} </span>
                </Col>
                <Col md="6" className="p-0">
                  <span style={{ fontSize: "1rem" }} className="font-weight-bold mr-1">
                    Last Name:
                  </span>
                  <span style={{ fontSize: "0.9rem" }}>{selectedCustomer?.individualUser?.lastName ?? "N/A"} </span>
                </Col>
              </Col>
              <Col md="12" className="w-100 d-flex justify-content-between p-0">
                <Col md="6" className="p-0">
                  <span style={{ fontSize: "1rem" }} className="font-weight-bold mr-1">
                    Email:
                  </span>
                  <span style={{ fontSize: "0.9rem" }}>{selectedCustomer?.email ?? "N/A"} </span>
                </Col>
                <Col md="6" className="p-0">
                  <span style={{ fontSize: "1rem" }} className="font-weight-bold mr-1">
                    Phone:
                  </span>
                  <span style={{ fontSize: "0.9rem" }}>{selectedCustomer?.phone ?? "N/A"} </span>
                </Col>
              </Col>
              <Col md="12" className="w-100 d-flex justify-content-between p-0">
                <Col md="6" className="p-0">
                  <span style={{ fontSize: "1rem" }} className="font-weight-bold mr-1">
                    Secondary Phone:
                  </span>
                  <span style={{ fontSize: "0.9rem" }}>
                    {selectedCustomer?.individualUser?.secondaryPhoneNumber ?? "N/A"}{" "}
                  </span>
                </Col>
                <Col md="6" className="p-0">
                  <span style={{ fontSize: "1rem" }} className="font-weight-bold mr-1">
                    Marital Status:
                  </span>
                  <span style={{ fontSize: "0.9rem" }}>
                    {selectedCustomer?.individualUser?.maritalStatus ?? "N/A"}{" "}
                  </span>
                </Col>
              </Col>
              <Col md="12" className="w-100 d-flex justify-content-between p-0">
                <Col md="6" className="p-0">
                  <span style={{ fontSize: "1rem" }} className="font-weight-bold mr-1">
                    Role:
                  </span>
                  <span style={{ fontSize: "0.9rem" }}>{selectedCustomer?.role ?? "N/A"} </span>
                </Col>
                <Col md="6" className="p-0">
                  <span style={{ fontSize: "1rem" }} className="font-weight-bold mr-1">
                    Source:
                  </span>
                  <span style={{ fontSize: "0.9rem" }}>{selectedCustomer?.source ?? "N/A"} </span>
                </Col>
              </Col>
              <Col md="12" className="w-100 d-flex justify-content-between p-0">
                <Col md="6" className="p-0">
                  <span style={{ fontSize: "1rem" }} className="font-weight-bold mr-1">
                    Address:
                  </span>
                  <span style={{ fontSize: "0.9rem" }}>
                    {selectedCustomer?.individualUser?.address?.houseNoAddress ?? "N/A"}{" "}
                  </span>
                </Col>
                <Col md="6" className="p-0">
                  <span style={{ fontSize: "1rem" }} className="font-weight-bold mr-1">
                    City:
                  </span>
                  <span style={{ fontSize: "0.9rem" }}>
                    {selectedCustomer?.individualUser?.address?.city ?? "N/A"}{" "}
                  </span>
                </Col>
              </Col>
              <Col md="12" className="w-100 d-flex justify-content-between p-0">
                <Col md="6" className="p-0">
                  <span style={{ fontSize: "1rem" }} className="font-weight-bold mr-1">
                    Country:
                  </span>
                  <span style={{ fontSize: "0.9rem" }}>
                    {selectedCustomer?.individualUser?.coutryOfResidence?.name ?? "N/A"}{" "}
                  </span>
                </Col>
                <Col md="6" className="p-0">
                  <span style={{ fontSize: "1rem" }} className="font-weight-bold mr-1">
                    Date Of Birth:
                  </span>
                  <span style={{ fontSize: "0.9rem" }}>{selectedCustomer?.individualUser?.dateOfBirth ?? "N/A"} </span>
                </Col>
              </Col>
              <Col md="12" className="w-100 d-flex justify-content-between p-0">
                <p className="font-weight-bold my-2" style={{ fontSize: "1.1rem" }}>
                  EMPLOYER DETAILS
                </p>
              </Col>
              <Col md="12" className="w-100 d-flex justify-content-between p-0">
                <Col md="6" className="p-0">
                  <span style={{ fontSize: "1rem" }} className="font-weight-bold mr-1">
                    Employer Name:
                  </span>
                  <span style={{ fontSize: "0.9rem" }}>
                    {selectedCustomer?.individualUser?.employmentDetail?.employerName ?? "N/A"}{" "}
                  </span>
                </Col>
                <Col md="6" className="p-0">
                  <span style={{ fontSize: "1rem" }} className="font-weight-bold mr-1">
                    Employer Address:
                  </span>
                  <span style={{ fontSize: "0.9rem" }}>
                    {selectedCustomer?.individualUser?.employmentDetail?.employerAddress ?? "N/A"}{" "}
                  </span>
                </Col>
              </Col>
              <Col md="12" className="w-100 d-flex justify-content-between p-0">
                <Col md="6" className="p-0">
                  <span style={{ fontSize: "1rem" }} className="font-weight-bold mr-1">
                    Occupation:
                  </span>
                  <span style={{ fontSize: "0.9rem" }}>
                    {selectedCustomer?.individualUser?.employmentDetail?.occupation ?? "N/A"}{" "}
                  </span>
                </Col>
              </Col>
              <Col md="12" className="w-100 d-flex justify-content-between p-0">
                <p className="font-weight-bold my-2" style={{ fontSize: "1.1rem" }}>
                  NEXT OF KIN DETAILS
                </p>
              </Col>
              <Col md="12" className="w-100 d-flex justify-content-between p-0">
                <Col md="6" className="p-0">
                  <span style={{ fontSize: "1rem" }} className="font-weight-bold mr-1">
                    Next Of Kin Name:
                  </span>
                  <span style={{ fontSize: "0.9rem" }}>
                    {selectedCustomer?.individualUser?.nokDetail?.name ?? "N/A"}{" "}
                  </span>
                </Col>
                <Col md="6" className="p-0">
                  <span style={{ fontSize: "1rem" }} className="font-weight-bold mr-1">
                    Next Of Kin Phone Number:
                  </span>
                  <span style={{ fontSize: "0.9rem" }}>
                    {selectedCustomer?.individualUser?.nokDetail?.phone ?? "N/A"}{" "}
                  </span>
                </Col>
              </Col>
              <Col md="12" className="w-100 d-flex justify-content-between p-0">
                <Col md="6" className="p-0">
                  <span style={{ fontSize: "1rem" }} className="font-weight-bold mr-1">
                    Email:
                  </span>
                  <span style={{ fontSize: "0.9rem" }}>
                    {selectedCustomer?.individualUser?.nokDetail?.email ?? "N/A"}{" "}
                  </span>
                </Col>
                <Col md="6" className="p-0">
                  <span style={{ fontSize: "1rem" }} className="font-weight-bold mr-1">
                    Relationship:
                  </span>
                  <span style={{ fontSize: "0.9rem" }}>
                    {selectedCustomer?.individualUser?.nokDetail?.relationship ?? "N/A"}{" "}
                  </span>
                </Col>
              </Col>
              <Col md="12" className="w-100 d-flex justify-content-between p-0">
                <Col md="6" className="p-0">
                  <span style={{ fontSize: "1rem" }} className="font-weight-bold mr-1">
                    Address:
                  </span>
                  <span style={{ fontSize: "0.9rem" }}>
                    {selectedCustomer?.individualUser?.nokDetail?.address ?? "N/A"}{" "}
                  </span>
                </Col>
                <Col md="6" className="p-0">
                  <span style={{ fontSize: "1rem" }} className="font-weight-bold mr-1">
                    City:
                  </span>
                  <span style={{ fontSize: "0.9rem" }}>
                    {selectedCustomer?.individualUser?.nokDetail?.city ?? "N/A"}{" "}
                  </span>
                </Col>
              </Col>
              <Col md="12" className="w-100 d-flex justify-content-between p-0">
                <Col md="6" className="p-0">
                  <span style={{ fontSize: "1rem" }} className="font-weight-bold mr-1">
                    State:
                  </span>
                  <span style={{ fontSize: "0.9rem" }}>
                    {selectedCustomer?.individualUser?.nokDetail?.state ?? "N/A"}{" "}
                  </span>
                </Col>
              </Col>
            </div>
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};
